import { useLocalStorage } from "@vueuse/core";
import { computed, onBeforeUnmount, ref, shallowRef, toRaw, watch } from "vue";
import { useI18n } from "vue-i18n";

import * as Sentry from "@sentry/vue";
import { defineStore } from "pinia";

import { AnalyticsEvent } from "@/enums/analytics";
import {
  TwilioCallDirection,
  TwilioCallErrors,
  TwilioDeviceState,
} from "@/enums/communicationProviders";
import { makeRandomString } from "@/helpers/formatting";
import { useDialerApiStore } from "@/services/dialer-api";
import { useAnalyticsStore } from "@/stores/analytics";

import type { NameId } from "@/models/clients";
import type { CallLog, CallOutPayload } from "@/models/communicationLogs";
import type { Call, Device } from "@twilio/voice-sdk";
import type { ComputedRef, Ref, ShallowRef, WritableComputedRef } from "vue";

export interface ActiveCallSession extends Partial<CallLog>, CallOutPayload {
  client_name: NameId | undefined;
  partner: string | undefined;
}

export interface CallsStore {
  device: ShallowRef<Device | null>;
  deviceState: ComputedRef<TwilioDeviceState>;
  activeCall: ShallowRef<Call | null>;
  incomingCall: ShallowRef<Call | null>;
  callDirection: Ref<TwilioCallDirection | null>;
  isMicrophoneMuted: Ref<boolean>;
  isRingtoneSilenced: Ref<boolean>;
  isKeypadVisible: Ref<boolean>;
  activeCallSession: Ref<ActiveCallSession | null>;
  callDialog: Ref<CallLog | null>;
  isDialing: Ref<boolean>;
  isPiPOpen: WritableComputedRef<boolean>;
  isPiPSupported: Ref<boolean>;
  isDeviceIdle: ComputedRef<boolean>;
  isCallActive: ComputedRef<boolean>;
  currentCall: ComputedRef<Call | null>;
  hasIncomingCall: ComputedRef<boolean>;
  hasOngoingCall: ComputedRef<boolean>;

  showCallTimer: Ref<boolean>;
  callStartedAt: WritableComputedRef<number | null>;
  playIncomingSound: () => void;
  stopIncomingSound: () => void;
  setIncomingCall: (call: Call) => void;
  setIncomingCallFromApi: (data: {
    sessionId: string;
    callerName: string;
    callerBusinessName: string;
    callerIntegrationPartner: string;
    calledNumber: string;
    from: string;
    applicationId?: string;
  }) => void;
  setActiveCall: (call: Call) => void;
  clearCallState: () => void;
  resetCallState: () => void;
  apiIncomingCallData: Ref<{
    sessionId: string;
    callerName: string;
    callerBusinessName: string;
    callerIntegrationPartner: string;
    calledNumber: string;
    from: string;
    applicationId?: string;
  } | null>;
  setDevice: (dev: Device | null) => void;
  togglePipState: (open: boolean) => void;
  setPiPSupported: (supported: boolean) => void;
  callRequestData: Ref<CallRequestPayload | null>;
  requestCall: (session: ActiveCallSession) => void;
  notifyCallStarted: () => void;
  notifyCallEnded: () => void;
  notifyIncomingCallHandled: () => void;
  notifyCallSilenced: () => void;
  broadcastPhoneNumberUpdate: (phoneNumber: string) => void;
  setInitiatingOutboundCall: () => void;
  clearInitiatingOutboundCall: () => void;
}

// Channels for cross-tab communication
enum COMMUNICATION_CHANNELS {
  CALL_REQUEST = "lf_twilio_call_request",
  PIP_STATE = "lf_twilio_pip_state",
}

export enum CALL_REQUEST_TYPES {
  INITIATE_CALL = "initial_call",
  CALL_STARTED = "call_started",
  CALL_ENDED = "call_ended",
  INCOMING_CALL_HANDLED = "incoming_call_handled",
  PHONE_NUMBER_UPDATE = "phone_number_update",
  CALL_SILENCED = "call_silenced",
}

export interface CallRequestPayload {
  type: CALL_REQUEST_TYPES;
  session?: ActiveCallSession;
  phoneNumber?: string;
  tabId?: string;
}

// Unique identifier for this tab to avoid processing own broadcasts
const TAB_ID = makeRandomString();

/**
 * Single source of truth for Twilio Dialer state
 * Used by both the main app and the PiP window
 */
export const useCallsStore = defineStore("calls", (): CallsStore => {
  const dialerApi = useDialerApiStore();

  // Device state - shallowRef because Device cannot be serialized to localStorage
  const device = shallowRef<Device | null>(null);
  const twilioStorage = useLocalStorage<{
    isPiPOpen: boolean;
    callStartedAt: number | null;
  }>("twilio", {
    isPiPOpen: false,
    callStartedAt: null,
  });

  // Call state
  const activeCall = shallowRef<Call | null>(null);
  const incomingCall = shallowRef<Call | null>(null);
  const callDirection = ref<TwilioCallDirection | null>(null);
  const isMicrophoneMuted = ref(false);
  const isRingtoneSilenced = ref(false);
  const isKeypadVisible = ref(false);
  const showCallTimer = ref(false);

  // API-driven incoming call metadata (for ephemeral device pattern)
  // Used when we receive incoming call notification from API before device exists
  const apiIncomingCallData = ref<{
    sessionId: string;
    callerName: string;
    callerBusinessName: string;
    callerIntegrationPartner: string;
    calledNumber: string;
    from: string;
    applicationId?: string;
  } | null>(null);

  // Cross-tab communication for call requests using native BroadcastChannel
  const callRequestData = ref<CallRequestPayload | null>(null);
  const callChannel = ref<BroadcastChannel | null>(null);
  const pipStateChannel = ref<BroadcastChannel | null>(null);

  // Session state
  const activeCallSession = ref<ActiveCallSession | null>(null);
  const callDialog = ref<CallLog | null>(null);
  const isDialing = ref(false);

  const isPiPSupported = ref(false);

  // Audio
  const incomingAudio = shallowRef<HTMLAudioElement | null>(null);
  const audioPromise = shallowRef<Promise<void> | undefined>(undefined);

  const { trackEvent } = useAnalyticsStore();
  const { t } = useI18n();

  // Optimistic flag: set when user clicks "Call", cleared when API state arrives.
  // Covers the brief gap before the API broadcasts 'initiating' via WebSocket.
  const isInitiatingOutboundCall = ref(false);

  const setInitiatingOutboundCall = () => {
    isInitiatingOutboundCall.value = true;
  };

  const clearInitiatingOutboundCall = () => {
    isInitiatingOutboundCall.value = false;
  };

  watch(() => dialerApi.callState, (newState) => {
    if (newState !== null) {
      isInitiatingOutboundCall.value = false;
    }
  });

  /**
   * deviceState derived from API call state, with local overrides for
   * the gap between Twilio SDK state changes and API broadcast delivery.
   */
  const deviceState = computed<TwilioDeviceState>(() => {
    const apiState = dialerApi.callState;

    if (apiState) {
      // Terminal states (busy, completed, etc.) keep the device "active"
      // so isCallActive stays true while TwilioPiPManager renders the
      // terminal screen — prevents the phone button from flickering back.
      if (apiState.is_terminal) {
        return TwilioDeviceState.Ongoing;
      }

      switch (apiState.status) {
        case "ringing":
          // Local activeCall means the SDK accepted before the API caught up
          return activeCall.value
            ? TwilioDeviceState.Ongoing
            : TwilioDeviceState.Incoming;
        case "initiating":
          return TwilioDeviceState.Initiating;
        case "in_call":
          return TwilioDeviceState.Ongoing;
      }
    }

    if (isInitiatingOutboundCall.value) {
      return TwilioDeviceState.Initiating;
    }

    return TwilioDeviceState.Idle;
  });

  const isPiPOpen = computed({
    get: () => twilioStorage.value.isPiPOpen,
    set: (value: boolean) => {
      twilioStorage.value.isPiPOpen = value;
    },
  });

  const callStartedAt = computed({
    get: () => twilioStorage.value.callStartedAt,
    set: (value: number | null) => {
      twilioStorage.value.callStartedAt = value;
    },
  });

  const isDeviceIdle = computed(
    () => deviceState.value === TwilioDeviceState.Idle
  );

  const isCallActive = computed(
    () =>
      deviceState.value === TwilioDeviceState.Ongoing ||
      deviceState.value === TwilioDeviceState.Incoming ||
      deviceState.value === TwilioDeviceState.Initiating
  );

  const currentCall = computed(() =>
    deviceState.value === TwilioDeviceState.Incoming
      ? incomingCall.value
      : activeCall.value
  );

  const hasIncomingCall = computed(
    () => deviceState.value === TwilioDeviceState.Incoming
  );

  const hasOngoingCall = computed(
    () => deviceState.value === TwilioDeviceState.Ongoing
  );


  const playIncomingSound = () => {
    if (!incomingAudio.value) {
      incomingAudio.value = new Audio("/sounds/incoming.mp3");
      incomingAudio.value.loop = true;
      incomingAudio.value.volume = 1;
    }

    try {
      audioPromise.value = incomingAudio.value.play();
    } catch (error) {
      trackEvent(AnalyticsEvent.TwilioError, {
        description: TwilioCallErrors.SoundError,
        explanation: t("ERRORS.TWILIO.INCOMING_SOUND_ERROR"),
      });
      Sentry.captureException(error);
    }
  };

  const stopIncomingSound = async () => {
    if (audioPromise.value) {
      try {
        await audioPromise.value;
      } catch {
        // Ignore errors from play() - browser may have blocked autoplay
      }
    }

    if (incomingAudio.value) {
      incomingAudio.value.pause();
      incomingAudio.value.currentTime = 0;
    }
  };

  const setIncomingCall = (call: Call) => {
    incomingCall.value = call;
    callDirection.value = TwilioCallDirection.Incoming;
    isRingtoneSilenced.value = false;
    playIncomingSound();
  };

  /**
   * Set incoming call state from API notification (ephemeral device pattern).
   * Used when we receive incoming call notification from API before device exists.
   * The actual Twilio Call object will be created when user accepts.
   */
  const setIncomingCallFromApi = (data: {
    sessionId: string;
    callerName: string;
    callerBusinessName: string;
    callerIntegrationPartner: string;
    calledNumber: string;
    from: string;
    applicationId?: string;
  }) => {
    apiIncomingCallData.value = data;
    callDirection.value = TwilioCallDirection.Incoming;
    isRingtoneSilenced.value = false;
    playIncomingSound();
  };

  const setActiveCall = (call: Call) => {
    activeCall.value = call;
    if (!callStartedAt.value) {
      callStartedAt.value = Date.now();
    }
    stopIncomingSound();
  };

  const clearCallState = () => {
    activeCall.value = null;
    incomingCall.value = null;
    apiIncomingCallData.value = null;
    callDirection.value = null;
    isMicrophoneMuted.value = false;
    isRingtoneSilenced.value = false;
    isDialing.value = false;
    activeCallSession.value = null;
    isKeypadVisible.value = false;
    showCallTimer.value = false;
    callStartedAt.value = null;
    stopIncomingSound();
    clearInitiatingOutboundCall();
  };

  /**
   * Force-reset all call state (local + API).
   * Used as an escape hatch when state gets stuck (e.g., after network issues).
   *
   * Cross-tab aware: SDK objects (Device, Call) only exist in the tab that
   * created them. When no local SDK objects exist, we broadcast CALL_ENDED
   * so the tab that owns the SDK can disconnect. A 500 ms fallback covers
   * single-tab stuck scenarios where no other tab clears the state.
   */
  const resetCallState = () => {
    // No local SDK objects — this tab can't disconnect the call directly.
    // Broadcast CALL_ENDED so the tab that owns the SDK handles cleanup.
    if (!activeCall.value && !incomingCall.value && !device.value?.isBusy) {
      notifyCallEnded();
      setTimeout(() => {
        if (!isDeviceIdle.value) {
          clearCallState();
          dialerApi.clearCallState();
        }
      }, 500);
      return;
    }

    // This tab owns the SDK — disconnect and let the normal ending flow
    // (handleCallEnded → startCallEnding) handle the graceful UI transition.
    // Calling clearCallState() here would wipe contact data before the
    // "Ending…" animation, causing a brief "unknown" flash.
    try {
      if (activeCall.value) {
        toRaw(activeCall.value).disconnect();
      }
      if (incomingCall.value) {
        toRaw(incomingCall.value).reject();
      }
      if (device.value && toRaw(device.value).isBusy) {
        toRaw(device.value).calls?.forEach((c) => c.disconnect());
      }
    } catch {
      // ignore — call may already be ended
    }

    notifyCallEnded();

    // Fallback: force-clear if the SDK disconnect event doesn't clean up in time.
    setTimeout(() => {
      if (!isDeviceIdle.value) {
        clearCallState();
        dialerApi.clearCallState();
      }
    }, 2000);
  };

  const setDevice = (dev: Device | null) => {
    device.value = dev;
  };

  const setPiPSupported = (supported: boolean) => {
    isPiPSupported.value = supported;
  };

  if (typeof BroadcastChannel !== "undefined") {
    pipStateChannel.value = new BroadcastChannel(
      COMMUNICATION_CHANNELS.PIP_STATE
    );
    pipStateChannel.value.onmessage = (event: MessageEvent<boolean>) => {
      isPiPOpen.value = event.data;
    };
  }

  const pipStateHandler = () => {
    isPiPOpen.value = false;
    pipStateChannel.value?.postMessage(false);
  };

  const togglePipState = (isOpen: boolean) => {
    isPiPOpen.value = isOpen;
    if (isOpen) {
      window.documentPictureInPicture?.window?.addEventListener(
        "pagehide",
        pipStateHandler
      );
      window.addEventListener("unload", pipStateHandler);
    }
    pipStateChannel.value?.postMessage(isOpen);
  };

  // Initialize BroadcastChannel (works in Pinia store unlike VueUse composable)
  if (typeof BroadcastChannel !== "undefined") {
    callChannel.value = new BroadcastChannel(
      COMMUNICATION_CHANNELS.CALL_REQUEST
    );
    callChannel.value.onmessage = (event: MessageEvent<CallRequestPayload>) => {
      // Skip processing our own broadcasts
      if (event.data.tabId === TAB_ID) {
        return;
      }
      callRequestData.value = event.data;
      isKeypadVisible.value = false;
    };
  }

  /**
   * Request a call from any tab - PiP will handle it
   */
  const requestCall = (session: ActiveCallSession) => {
    // Must serialize to plain object - Vue Proxy objects can't be cloned by BroadcastChannel
    const plainSession = JSON.parse(JSON.stringify(session));
    callChannel.value?.postMessage({
      type: CALL_REQUEST_TYPES.INITIATE_CALL,
      session: plainSession,
      tabId: TAB_ID,
    } as CallRequestPayload);
  };

  /**
   * Notify other tabs that a call has started
   */
  const notifyCallStarted = () => {
    callChannel.value?.postMessage({
      type: CALL_REQUEST_TYPES.CALL_STARTED,
      tabId: TAB_ID,
    } as CallRequestPayload);
  };

  /**
   * Notify other tabs that a call has ended
   */
  const notifyCallEnded = () => {
    isKeypadVisible.value = false;

    callChannel.value?.postMessage({
      type: CALL_REQUEST_TYPES.CALL_ENDED,
      tabId: TAB_ID,
    } as CallRequestPayload);
  };

  /**
   * Notify other tabs that this tab is handling the incoming call
   */
  const notifyIncomingCallHandled = () => {
    callChannel.value?.postMessage({
      type: CALL_REQUEST_TYPES.INCOMING_CALL_HANDLED,
      tabId: TAB_ID,
    } as CallRequestPayload);
  };

  /**
   * Notify other tabs that the incoming call was silenced/muted
   */
  const notifyCallSilenced = () => {
    callChannel.value?.postMessage({
      type: CALL_REQUEST_TYPES.CALL_SILENCED,
      tabId: TAB_ID,
    } as CallRequestPayload);
  };

  /**
   * Broadcast phone number update to other tabs
   */
  const broadcastPhoneNumberUpdate = (phoneNumber: string) => {
    callChannel.value?.postMessage({
      type: CALL_REQUEST_TYPES.PHONE_NUMBER_UPDATE,
      phoneNumber,
      tabId: TAB_ID,
    } as CallRequestPayload);
  };

  onBeforeUnmount(() => {
    window.removeEventListener("unload", pipStateHandler);
  });

  return {
    device,
    deviceState,
    activeCall,
    incomingCall,
    callDirection,
    isMicrophoneMuted,
    isRingtoneSilenced,
    activeCallSession,
    callDialog,
    isDialing,
    isPiPOpen,
    isPiPSupported,

    isKeypadVisible,
    showCallTimer,
    callStartedAt,

    isDeviceIdle,
    isCallActive,
    currentCall,
    hasIncomingCall,
    hasOngoingCall,

    playIncomingSound,
    stopIncomingSound,
    setIncomingCall,
    setIncomingCallFromApi,
    setActiveCall,
    clearCallState,
    resetCallState,
    setDevice,
    apiIncomingCallData,
    togglePipState,
    setPiPSupported,

    callRequestData,
    requestCall,
    notifyCallStarted,
    notifyCallEnded,
    notifyIncomingCallHandled,
    notifyCallSilenced,
    broadcastPhoneNumberUpdate,
    setInitiatingOutboundCall,
    clearInitiatingOutboundCall,
  };
});
